import React from 'react';
import './AdminMasterDetail.css';

export type SortDirection = 'asc' | 'desc';

export interface HeaderAction {
  key: string;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

export interface DataGridColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  render: (row: T) => React.ReactNode;
}

export interface DataGridRowAction<T> {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: (row: T) => void;
  tone?: 'default' | 'destructive';
  isDisabled?: (row: T) => boolean;
}

interface DataGridProps<T extends { id: number }> {
  rows: T[];
  columns: DataGridColumn<T>[];
  selectedRowId: number | null;
  onRowClick: (row: T) => void;
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  showSelection?: boolean;
  sortKey: string;
  sortDirection: SortDirection;
  onSort: (columnKey: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (nextPage: number) => void;
  emptyMessage: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  rowActions?: DataGridRowAction<T>[];
  alwaysShowRowActions?: boolean;
}

interface FilterBarProps {
  searchValue: string;
  searchPlaceholder?: string;
  onSearchChange: (value: string) => void;
  filters: FilterConfig[];
}

interface HeaderProps {
  title: string;
  subtitle: string;
  actions: HeaderAction[];
}

export const AdminPageHeader: React.FC<HeaderProps> = ({ title, subtitle, actions }) => {
  return (
    <div className="amd-header">
      <div className="amd-header-copy">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <div className="amd-header-actions">
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            className={`amd-btn amd-btn-${action.variant || 'secondary'}`}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const AdminFilterBar: React.FC<FilterBarProps> = ({
  searchValue,
  searchPlaceholder,
  onSearchChange,
  filters,
}) => {
  return (
    <div className="amd-filters">
      <div className="amd-search-wrap">
        <input
          type="text"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder || 'Search...'}
          className="amd-search"
        />
      </div>
      <div className="amd-filter-list">
        {filters.map((filter) => (
          <label key={filter.key} className="amd-filter-item">
            <span>{filter.label}</span>
            <select
              value={filter.value}
              onChange={(event) => filter.onChange(event.target.value)}
            >
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </div>
  );
};

export const AdminStatusBadge: React.FC<{ status: 'Active' | 'Draft' | 'Archived' }> = ({ status }) => {
  const className =
    status === 'Active'
      ? 'amd-badge amd-badge-active'
      : status === 'Draft'
      ? 'amd-badge amd-badge-draft'
      : 'amd-badge amd-badge-archived';

  return <span className={className}>{status}</span>;
};

export const AdminDataGrid = <T extends { id: number }>({
  rows,
  columns,
  selectedRowId,
  onRowClick,
  selectedIds,
  onSelectedIdsChange,
  showSelection = true,
  sortKey,
  sortDirection,
  onSort,
  page,
  pageSize,
  totalCount,
  onPageChange,
  emptyMessage,
  emptyActionLabel,
  onEmptyAction,
  rowActions,
  alwaysShowRowActions = false,
}: DataGridProps<T>) => {
  const [focusedRowId, setFocusedRowId] = React.useState<number | null>(null);
  const allVisibleSelected =
    showSelection && rows.length > 0 && rows.every((item) => selectedIds.includes(item.id));
  const hasAnyVisibleSelected =
    showSelection && rows.some((item) => selectedIds.includes(item.id));

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const next = selectedIds.filter((id) => !rows.some((row) => row.id === id));
      onSelectedIdsChange(next);
      return;
    }

    const merged = new Set<number>([...selectedIds, ...rows.map((row) => row.id)]);
    onSelectedIdsChange(Array.from(merged));
  };

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectedIdsChange(selectedIds.filter((existing) => existing !== id));
      return;
    }

    onSelectedIdsChange([...selectedIds, id]);
  };

  if (rows.length === 0) {
    return (
      <div className="amd-empty-state">
        <p>{emptyMessage}</p>
        {emptyActionLabel && onEmptyAction && (
          <button type="button" className="amd-btn amd-btn-primary" onClick={onEmptyAction}>
            {emptyActionLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="amd-grid-shell">
      <div className="amd-grid-table-wrap">
        <table className="amd-grid-table">
          <thead>
            <tr>
              {showSelection && (
                <th className="amd-checkbox-col">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = !allVisibleSelected && hasAnyVisibleSelected;
                      }
                    }}
                    onChange={toggleSelectAllVisible}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th key={column.key} style={{ width: column.width }}>
                  {column.sortable ? (
                    <button
                      type="button"
                      className="amd-sort-btn"
                      onClick={() => onSort(column.key)}
                      aria-label={`Sort by ${column.label}${sortKey === column.key ? `, currently ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
                      title={sortKey === column.key ? `Sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : 'Click to sort'}
                    >
                      {column.label}
                      {sortKey === column.key && (
                        <span>{sortDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
              {rowActions && rowActions.length > 0 && <th className="amd-row-actions-col">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelected = selectedRowId === row.id;
              const isFocused = focusedRowId === row.id;
              return (
                <tr
                  key={row.id}
                  className={`${isSelected ? 'is-selected' : ''} ${isFocused ? 'is-focused' : ''}`.trim()}
                  onClick={() => onRowClick(row)}
                  tabIndex={0}
                  onFocus={() => setFocusedRowId(row.id)}
                  onBlur={() => setFocusedRowId((current) => (current === row.id ? null : current))}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowClick(row);
                    }
                  }}
                >
                  {showSelection && (
                    <td
                      className="amd-checkbox-col"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleSelect(row.id)}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={`${row.id}-${column.key}`}>{column.render(row)}</td>
                  ))}
                  {rowActions && rowActions.length > 0 && (
                    <td
                      className="amd-row-actions-cell"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <div className={`amd-row-actions-wrap ${alwaysShowRowActions ? 'is-always-visible' : ''}`.trim()}>
                        {rowActions.map((action) => (
                          <button
                            key={`${row.id}-${action.key}`}
                            type="button"
                            className={`amd-row-action-btn ${action.tone === 'destructive' ? 'is-destructive' : ''}`}
                            title={action.label}
                            aria-label={action.label}
                            onClick={() => action.onClick(row)}
                            disabled={action.isDisabled ? action.isDisabled(row) : false}
                          >
                            {action.icon}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="amd-pagination">
        <div>
          Page {page} of {totalPages}
        </div>
        <div className="amd-pagination-actions">
          <button
            type="button"
            className="amd-btn amd-btn-ghost"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            Previous
          </button>
          <button
            type="button"
            className="amd-btn amd-btn-ghost"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
